import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Store } from '@ngxs/store'
import { TranslateService } from '@ngx-translate/core'
import { Observable, Subject } from 'rxjs'
import { filter, take, takeUntil } from 'rxjs/operators'

import {
  AddPremadeFieldToFormLibrary,
  CopyInspectionTypeDetailRow,
  DropToInspectionTypeDetail,
  GetTemplateLibraryPieces,
  RemoveInspectionTypeDetailRow,
  ToggleInspectionAdditionalInformation,
} from 'src/app/app.state'
import { Constants, FORM_LIBRARY_DROP_TYPE, MENU_OPTION_KEY, NOTIFICATION_TYPES } from 'src/app/constants/internationalized-constants-en'
import { InspectionType, InspectionTypeFormData, unmodified } from 'src/app/views/inspection-type/inspection-type.component'
import { AddPieceDialogComponent } from '../add-piece-dialog/add-piece-dialog.component'
import { DialogComponent } from '../dialog/dialog.component'
import { InspectionDetailRowCheckboxEditDialogComponent } from '../inspection-detail-row-checkbox-edit-dialog/inspection-detail-row-checkbox-edit-dialog.component'
import { InspectionDetailRowDropdownEditDialogComponent } from '../inspection-detail-row-dropdown-edit-dialog/inspection-detail-row-dropdown-edit-dialog.component'
import { InspectionDetailRowPhotoEditDialogComponent } from '../inspection-detail-row-photo-edit-dialog/inspection-detail-row-photo-edit-dialog.component'
import { InspectionDetailRowEditDialogComponent } from '../inspection-detail-row-edit-dialog/inspection-detail-row-edit-dialog.component'
import { MenuComponent, MenuOption } from '../menu/menu.component'
import { NotificationSnackbarService } from '../notification-snackbar/services/notification-snackbar.service'
import { FormDataModel } from '../tree/models/tree.model'
import { KeyboardFormLibraryDragService } from '../form-library/services/keyboard-form-library-drag/keyboard-form-library-drag.service'

export interface InspectionRowEditDialogData {
  inspectionTypeIndex: number
  formDataIndex: number
  row: InspectionTypeFormData
  // This property to mark the action dispatched to the store as edit or drag and drop
  isUpdate: boolean
}
interface FormDataWithUnit extends InspectionTypeFormData {
  availableUnits: string[]
  selectedUnit: string
}

interface InspectionTypeWithUnit extends InspectionType {
  formData: FormDataWithUnit[]
}

export const MENU_INSPECTION_TYPE_DETAIL_ROW = {
  Edit: { text: MENU_OPTION_KEY.EDIT },
  Copy: { text: MENU_OPTION_KEY.COPY },
  // ECT2-1326: Set disable true to temporarily disable this option
  // EVIRPX-5: Need to make code changes to ensure the "Add to Library" feature works correctly when this option is enabled
  'Add to library': { text: MENU_OPTION_KEY.ADD_TO_LIBRARY, disable: true },
  Delete: { text: MENU_OPTION_KEY.DELETE }
}

export const INSPECTION_DETAIL_TYPE = {
  TEXT: 'text',
  NUMBERIC: 'int',
  CHECKBOX: 'bool',
  DROPDOWN: 'select',
  FLOAT: 'float',
  PHOTO: 'photo',
}

@Component({
  selector: 'app-inspection-type-detail',
  templateUrl: './inspection-type-detail.component.html',
  styleUrls: ['./inspection-type-detail.component.scss'],
})
export class InspectionTypeDetailComponent implements OnInit, OnChanges, OnDestroy {
  @Input() inspectionType: InspectionType = null
  @Input() index: number
  @Input() isDraggingFormLibraryField: boolean
  @Input() isAccessGranted: boolean = false
  @Input() maxImageCount: number

  @ViewChild('mainDetails') mainDetailsEl: ElementRef<HTMLElement>

  transformedInspectionType: InspectionTypeWithUnit
  keyboardDragActive: boolean = false
  keyboardDropIndex: number = 0

  private destroy$ = new Subject<void>()

  private adminOnlyOptions: MenuOption[] = [
    MENU_INSPECTION_TYPE_DETAIL_ROW.Copy,
    MENU_INSPECTION_TYPE_DETAIL_ROW['Add to library']
  ]

  get menuInspectionTypeDetailRow(): MenuOption[] {
    return Object.values(MENU_INSPECTION_TYPE_DETAIL_ROW).filter(menuItem => this.isAccessGranted || !this.adminOnlyOptions.map(item => item.text).includes(menuItem.text))
  }

  get inspectionFormData(): FormDataWithUnit[] {
    if (this.transformedInspectionType && this.transformedInspectionType.formData && this.transformedInspectionType.formData.length) {
      return this.transformedInspectionType.formData
    }

    return []
  }

  get isLastItemCheckbox(): boolean {
    if (this.inspectionFormData.length > 0) {
      return (this.inspectionFormData[this.inspectionFormData.length - 1] || {}).inspectionDetailType === 'bool'
    } else {
      return false
    }
  }

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private snackbarService: NotificationSnackbarService,
    private translateService: TranslateService,
    private keyboardFormLibraryDragService: KeyboardFormLibraryDragService,
  ) { }

  ngOnInit(): void {
    this.keyboardFormLibraryDragService.state$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      this.keyboardDragActive = state.active

      if (!state.active) {
        return
      }

      this.keyboardDropIndex = this.clampDropIndex(this.keyboardDropIndex)

      if (state.inDestination) {
        this.focusKeyboardDropTarget()
      }
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.inspectionType && (changes.inspectionType.currentValue !== changes.inspectionType.previousValue)) {
      this.transformedInspectionType = this.convertInspectionType(changes.inspectionType.currentValue)
      this.keyboardDropIndex = this.clampDropIndex(this.keyboardDropIndex)
    }
  }

  get photoLimit() {
    return `(0/${this.maxImageCount})`
  }

  async handleContextMenuSelected(menuItem: string, selectedIndex: number) {
    switch (menuItem) {
      case MENU_INSPECTION_TYPE_DETAIL_ROW.Edit.text: {
        if (await unmodified.isInspectionValueUnmodified(this.inspectionType, this.dialog, this.translateService)) {
          setTimeout(() => MenuComponent.lastTriggerElement?.focus(), 50)
          return
        }

        const inspectionDetailType = this.inspectionType.formData[selectedIndex].inspectionDetailType
        const editDialog = this.getEditDialog(inspectionDetailType)
        this.dialog.open(editDialog, {
          data: {
            row: this.inspectionType.formData[selectedIndex],
            formDataIndex: selectedIndex,
            inspectionTypeIndex: this.index,
            isUpdate: true,
          }
        }).afterClosed().subscribe(() => {
          setTimeout(() => {
            if (MenuComponent.lastTriggerElement && document.contains(MenuComponent.lastTriggerElement)) {
              MenuComponent.lastTriggerElement.focus()
            } else {
              const ellipsisEls = document.querySelectorAll('.detail-box .more_horiz')
              if (ellipsisEls.length > 0) {
                const targetIndex = Math.min(selectedIndex, ellipsisEls.length - 1)
                const targetEllipsis = ellipsisEls[targetIndex] as HTMLElement
                targetEllipsis.focus()
              }
            }
          }, 100)
        })
        break
      }

      case MENU_INSPECTION_TYPE_DETAIL_ROW.Delete.text: {
        if (await unmodified.isInspectionValueUnmodified(this.inspectionType, this.dialog, this.translateService)) {
          setTimeout(() => MenuComponent.lastTriggerElement?.focus(), 50)
          return
        }

        this.openConfirmDialog(selectedIndex, () => this.deleteDetailRow(selectedIndex))
        break
      }

      case MENU_INSPECTION_TYPE_DETAIL_ROW.Copy.text:
        this.store.dispatch(new CopyInspectionTypeDetailRow(this.inspectionType.formData[selectedIndex]))
        setTimeout(() => {
          if (MenuComponent.lastTriggerElement && document.contains(MenuComponent.lastTriggerElement)) {
            MenuComponent.lastTriggerElement.focus()
          } else {
            const ellipsisEls = document.querySelectorAll('.detail-box .more_horiz')
            if (ellipsisEls.length > selectedIndex) {
              const targetEllipsis = ellipsisEls[selectedIndex] as HTMLElement
              targetEllipsis.focus()
            }
          }
        }, 100)
        break

      case MENU_INSPECTION_TYPE_DETAIL_ROW['Add to library'].text:
        const inspectionTypeDetailRow = this.inspectionType.formData[selectedIndex]
        this.openAddPremadeFieldDialog(inspectionTypeDetailRow)
        break

      default: {
        break
      }
    }
  }

  openConfirmDialog(index: number, successCallback: () => void) {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        onClose: () => { dialogRef.close() },
        onAccept: () => {
          successCallback()
          dialogRef.close()
        },
        cancelText: this.translateService.instant('CANCEL'),
        acceptText: this.translateService.instant('OK'),
        title: this.translateService.instant('INSPECTION_FORM_DELETE_CONFIRM_TITLE', { name: this.inspectionType.formData[index].fieldNameLabel }),
        message: '',
        focusAccept: true,
      },
    })

    dialogRef.afterClosed().subscribe(() => {
      setTimeout(() => {
        if (MenuComponent.lastTriggerElement && document.contains(MenuComponent.lastTriggerElement)) {
          MenuComponent.lastTriggerElement.focus()
        } else {
          const ellipsisEls = document.querySelectorAll('.detail-box .more_horiz')
          if (ellipsisEls.length > 0) {
            const targetIndex = Math.min(index, ellipsisEls.length - 1)
            const targetEllipsis = ellipsisEls[targetIndex] as HTMLElement
            targetEllipsis.focus()
          }
        }
      }, 50)
    })
  }

  deleteDetailRow<T>(index: number): Observable<T> {
    return this.store.dispatch(new RemoveInspectionTypeDetailRow(index))
  }

  getEditDialog(inspectionDetailType: string): any {
    switch (inspectionDetailType) {
      case INSPECTION_DETAIL_TYPE.TEXT:
      case INSPECTION_DETAIL_TYPE.NUMBERIC:
      case INSPECTION_DETAIL_TYPE.FLOAT:
        return InspectionDetailRowEditDialogComponent
      case INSPECTION_DETAIL_TYPE.CHECKBOX:
        return InspectionDetailRowCheckboxEditDialogComponent
      case INSPECTION_DETAIL_TYPE.DROPDOWN:
        return InspectionDetailRowDropdownEditDialogComponent
      case INSPECTION_DETAIL_TYPE.PHOTO:
        return InspectionDetailRowPhotoEditDialogComponent
      default:
        return
    }
  }

  async drop(event: CdkDragDrop<FormDataModel[]>) {
    // Allow keyboard drops where no pointer hover is reported.
    const hasDistance = !!event?.distance && typeof event.distance.x === 'number' && typeof event.distance.y === 'number'
    const eventType = (event as any)?.event?.type || ''
    const isKeyboardEventType = typeof eventType === 'string' && eventType.toLowerCase().includes('key')
    const isKeyboardDrop = !event.isPointerOverContainer
      && (isKeyboardEventType || (hasDistance && event.distance.x === 0 && event.distance.y === 0))
    if (!(event && (event.isPointerOverContainer || isKeyboardDrop))) {
      return
    }

    await this.handleDropData(event.item?.data, event.currentIndex)
  }

  async onKeyboardDropAreaKeydown(event: KeyboardEvent) {
    const dragState = this.keyboardFormLibraryDragService.snapshot
    if (!dragState.active || !dragState.inDestination) {
      return
    }

    const key = this.normalizeKeyboardKey(event.key)

    if (key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      this.keyboardFormLibraryDragService.cancel()
      return
    }

    if (key === 'ArrowUp') {
      event.preventDefault()
      event.stopPropagation()
      this.keyboardDropIndex = this.clampDropIndex(this.keyboardDropIndex - 1)
      this.focusKeyboardDropTarget()
      return
    }

    if (key === 'ArrowDown') {
      event.preventDefault()
      event.stopPropagation()
      this.keyboardDropIndex = this.clampDropIndex(this.keyboardDropIndex + 1)
      this.focusKeyboardDropTarget()
      return
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault()
      event.stopPropagation()
      const shouldRestoreNow = await this.handleDropData(dragState.dragData, this.keyboardDropIndex)
      if (shouldRestoreNow) {
        this.keyboardFormLibraryDragService.completeDrop()
      }
    }
  }

  showKeyboardPreviewAt(index: number): boolean {
    const dragState = this.keyboardFormLibraryDragService.snapshot
    return !!(dragState.active && dragState.inDestination && this.keyboardDropIndex === index)
  }

  isKeyboardDropTarget(index: number): boolean {
    const dragState = this.keyboardFormLibraryDragService.snapshot
    return !!(dragState.active && dragState.inDestination && this.keyboardDropIndex === index)
  }

  get keyboardPreviewLabel(): string {
    const dragState = this.keyboardFormLibraryDragService.snapshot
    return dragState.dragData?.previewLabel || ''
  }

  private async handleDropData(dragData: any, dropIndex: number): Promise<boolean> {
    if (await unmodified.isInspectionValueUnmodified(this.inspectionType, this.dialog, this.translateService)) {
      return true
    }

    const rowData = dragData?.dropValue
    // Return if row data doesn't exist or empty
    if (!(rowData && Object.keys(rowData).length > 0)) {
      return true
    }

    // Return if drop type is invalid
    if (!(dragData?.dropType && Object.values(FORM_LIBRARY_DROP_TYPE).includes(dragData.dropType))) {
      return true
    }

    // Handle drop for premade field
    if (dragData.dropType === FORM_LIBRARY_DROP_TYPE.PREMADE_FIELD) {
      this.store.dispatch(new DropToInspectionTypeDetail(
        this.index,
        dropIndex,
        rowData,
        true,
      ))
      return true
    }

    const editDialog = this.getEditDialog(rowData.inspectionDetailType)
    // Return if there is no dialog for this inspection detail type
    if (!editDialog) {
      return true
    }

    this.dialog.open(editDialog, {
      data: {
        row: rowData,
        formDataIndex: dropIndex,
        inspectionTypeIndex: this.index,
        isUpdate: false,
      },
    }).afterClosed().pipe(take(1)).subscribe(() => {
      this.keyboardFormLibraryDragService.completeDrop()
    })

    return false
  }

  private focusKeyboardDropTarget() {
    setTimeout(() => {
      const rows = this.mainDetailsEl?.nativeElement?.querySelectorAll('.detail-box') as NodeListOf<HTMLElement>
      if (rows && rows.length > 0) {
        const target = rows[Math.min(this.keyboardDropIndex, rows.length - 1)]
        target?.focus()
        return
      }

      this.mainDetailsEl?.nativeElement?.focus()
    }, 0)
  }

  private clampDropIndex(index: number): number {
    const maxIndex = this.inspectionFormData.length
    return Math.max(0, Math.min(index, maxIndex))
  }

  private normalizeKeyboardKey(key: string): string {
    if (key === 'Spacebar') {
      return ' '
    }

    return key
  }

  onCheckboxClicked(key: string) {
    this.store.dispatch(new ToggleInspectionAdditionalInformation(this.index, key))
  }

  openAddPremadeFieldDialog(inspectionTypeDetailRow: InspectionTypeFormData) {
    this.dialog.open(AddPieceDialogComponent, {
      data: {
        name: inspectionTypeDetailRow.fieldNameLabel,
        hasNodeChildren: null,
        isPremadeField: true,
        title: this.translateService.instant('INSPECTION_PIECE_ADD_TITLE'),
      },
      panelClass: 'add-premade-field-dialog',
    }).afterClosed().pipe(filter(data => !!data))
      .subscribe(result => {
        const { name, isAdminViewOnly } = result
        this.handleAddPremadeField(name, isAdminViewOnly, inspectionTypeDetailRow)
      })
  }

  private handleAddPremadeField(premadeFieldName: string, isAdminViewOnly: boolean, inspectionTypeDetailRow: InspectionTypeFormData) {
    this.store.dispatch(new AddPremadeFieldToFormLibrary(premadeFieldName, isAdminViewOnly, inspectionTypeDetailRow)).subscribe(
      value => { },
      error => {
        this.snackbarService.openNotificationSnackbar(
          this.translateService.instant('INSPECTION_PIECE_ADD_FAIL_MSG', { name: premadeFieldName }),
          NOTIFICATION_TYPES.FAILED
        )
      },
      () => {
        this.store.dispatch(new GetTemplateLibraryPieces())
        this.snackbarService.openNotificationSnackbar(
          this.translateService.instant('INSPECTION_PIECE_ADD_SUCCESS_MSG', { name: premadeFieldName }),
          NOTIFICATION_TYPES.SUCCESS, null, Constants.snackbar.duration,
        )
      }
    )
  }

  private convertInspectionType(input: InspectionType): InspectionTypeWithUnit {
    if (!input) { return null }
    return {
      ...input,
      formData: (input.formData || []).map(this.convertInspectionTypeFormData.bind(this))
    }
  }

  private convertInspectionTypeFormData(formData: InspectionTypeFormData): FormDataWithUnit {
    const availableUnits = Array.isArray(formData.inspectionDetailUnitConversionPair)
      ? formData.inspectionDetailUnitConversionPair
      : []

    return {
      ...formData,
      availableUnits: availableUnits || null,
      selectedUnit: availableUnits ? availableUnits[0] : null,
    }
  }
}
